// Mock API service. In a real application, this would make HTTP requests to a backend.
import { apiClient } from '../config/api';

// MOCK DATABASE - In a real app, this would be on a server.
let mockDb = {
    dealerships: [
        { id: '1', name: 'City Toyota', locations: [], onboardingStatus: {}, contactPlan: null, signoffStatus: {} },
        { id: '2', name: 'Mountain Honda', locations: [], onboardingStatus: {}, contactPlan: null, signoffStatus: {} },
    ],
    templates: {
        checklist: { sales: [], service: [] },
        factfind: { sales: [], service: [] },
        links: { sales: [], service: [] },
        contactPlan: { sales: [], service: [] },
        signoff: { 
            sales: { messageTypes: [], locations: [], assignments: {} }, 
            service: { messageTypes: [], locations: [], assignments: {} } 
        }
    },
    dropdownOptions: {}
};

const apiDelay = 300;

// Helper to simulate network delay and deep copy to prevent mutation issues
const withDelay = (data) => new Promise(resolve => setTimeout(() => resolve(JSON.parse(JSON.stringify(data))), apiDelay));

// --- FETCH (Read) Operations ---

export const fetchDealerships = async () => {
    console.log('API: Fetching dealerships...');
    try {
        // Fetch both dealer locations and groups data
        const [realData, groupsData] = await Promise.all([
            apiClient.get('/dealer-locations-with-addresses'),
            apiClient.get('/groups')
        ]);

        if (realData && groupsData) {

            // Create a mapping of group IDs to group names
            const groupNameMap = {};
            groupsData.forEach(group => {
                groupNameMap[group.id] = group.name;
            });

            // Transform real data to match the expected frontend format
            const groupedData = {};

            realData.forEach(location => {
                const groupId = location.group_id || 'ungrouped';
                if (!groupedData[groupId]) {
                    groupedData[groupId] = {
                        id: groupId,
                        name: groupNameMap[groupId] || (groupId === 'ungrouped' ? 'Ungrouped Locations' : groupId),
                        locations: [],
                        onboardingStatus: {},
                        contactPlan: null,
                        signoffStatus: {}
                    };
                }

                // Add location with address info
                groupedData[groupId].locations.push({
                    id: location.id,
                    name: location.name,
                    trading_name: location.trading_name,
                    factfindData: {},
                    linksData: {},
                    address: location.street_name ? {
                        id: location.address_id,
                        building_name: location.building_name,
                        building_number: location.building_number,
                        street_name: location.street_name,
                        locality: location.locality,
                        town_city: location.town_city,
                        county: location.county,
                        postcode: location.postcode,
                        country: location.country,
                        address_type: location.address_type
                    } : null
                });
            });

            const dealerships = Object.values(groupedData);
            console.log('Real dealerships loaded:', dealerships);
            return dealerships;
        } else {
            console.warn('Failed to fetch real dealerships or groups, falling back to mock data');
            return withDelay(mockDb.dealerships);
        }
    } catch (error) {
        console.error('Error fetching real dealerships:', error);
        return withDelay(mockDb.dealerships);
    }
};

export const fetchChecklistTemplate = async () => {
    console.log('API: Fetching checklist template from master-checklist...');
    try {
        const data = await apiClient.get('/master-checklist');
        return data;
    } catch (error) {
        console.warn('Failed to fetch checklist template from API, falling back to mock data');
        console.error('Error fetching checklist template:', error);
        return withDelay(mockDb.templates.checklist);
    }
};

export const fetchFactfindTemplate = async () => {
    console.log('API: Fetching factfind template...');
    try {
        const templateData = await apiClient.get('/admin/templates/factfind');
        return templateData; // Already in {sales: [], service: []} format
    } catch (error) {
        console.warn('Failed to fetch factfind template from API, falling back to mock data');
        console.error('Error fetching factfind template:', error);
        return withDelay(mockDb.templates.factfind);
    }
};

export const fetchLinksTemplate = async () => {
    console.log('API: Fetching links template...');
    try {
        const adminLinks = await apiClient.get('/admin/links');

        // Transform admin links into template format for setup forms
        const salesLinks = adminLinks
            .filter(link => link.link_type === 'sales')
            .map(link => ({
                id: link.id,
                link_key: link.link_key,
                link_label: link.link_label,
                display_order: link.display_order,
                is_active: link.is_active,
                is_required: link.is_required
            }))
            .sort((a, b) => a.display_order - b.display_order);

        const serviceLinks = adminLinks
            .filter(link => link.link_type === 'service')
            .map(link => ({
                id: link.id,
                link_key: link.link_key,
                link_label: link.link_label,
                display_order: link.display_order,
                is_active: link.is_active,
                is_required: link.is_required
            }))
            .sort((a, b) => a.display_order - b.display_order);

        return { sales: salesLinks, service: serviceLinks };
    } catch (error) {
        console.warn('Failed to fetch links template from API, falling back to mock data');
        console.error('Error fetching links template:', error);
        return withDelay(mockDb.templates.links);
    }
};

export const fetchContactPlanTemplate = async () => {
    console.log('API: Fetching contact plan template...');
    try {
        const [salesData, serviceData] = await Promise.all([
            apiClient.get('/contact-messages?type=sales'),
            apiClient.get('/contact-messages?type=service')
        ]);

        if (salesData && serviceData) {

            // Transform the database format to match the expected contact plan structure
            const transformedTemplate = {
                sales: salesData.map((item) => ({
                    id: `cp-sales-${item.id}`,
                    messageName: item.message_name,
                    sendWhen: item.default_send_when || { qty: 1, unit: 'day', timing: 'after', event: 'Contract Signed' },
                    notes: item.notes || '',
                    feed: item.default_feed || 'Email',
                    messageContent: item.message_content
                })),
                service: serviceData.map((item) => ({
                    id: `cp-service-${item.id}`,
                    messageName: item.message_name,
                    sendWhen: item.default_send_when || { qty: 1, unit: 'day', timing: 'after', event: 'Service Due' },
                    notes: item.notes || '',
                    feed: item.default_feed || 'Email',
                    messageContent: item.message_content
                }))
            };

            return transformedTemplate;
        }
        console.warn('Failed to fetch contact plan template from API, falling back to mock data');
        return withDelay(mockDb.templates.contactPlan);
    } catch (error) {
        console.error('Error fetching contact plan template:', error);
        return withDelay(mockDb.templates.contactPlan);
    }
};

export const fetchSignoffTemplate = async () => {
    console.log('API: Fetching signoff template...');
    try {
        const [salesData, serviceData] = await Promise.all([
            apiClient.get('/templates/signoff/sales').catch(() => []),
            apiClient.get('/templates/signoff/service').catch(() => [])
        ]);

        // Transform the new API format to match the expected signoff page structure
        const transformedTemplate = {
            sales: {
                messageTypes: salesData.map(item => item.label),
                locations: ['Location 1', 'Location 2', 'Location 3'], // Default locations - will be configurable
                assignments: {}
            },
            service: {
                messageTypes: serviceData.map(item => item.label),
                locations: ['Location 1', 'Location 2', 'Location 3'], // Default locations - will be configurable
                assignments: {}
            }
        };

        // Initialize assignments structure
        ['sales', 'service'].forEach(type => {
            const data = type === 'sales' ? salesData : serviceData;
            transformedTemplate[type].messageTypes.forEach(msgType => {
                transformedTemplate[type].assignments[msgType] = {};
                transformedTemplate[type].locations.forEach(loc => {
                    transformedTemplate[type].assignments[msgType][loc] = {
                        email: '',
                        name: '',
                        signedOff: false
                    };
                });
            });
        });

        return transformedTemplate;
    } catch (error) {
        console.warn('Failed to fetch signoff template from API, falling back to mock data');
        console.error('Error fetching signoff template:', error);
        return withDelay(mockDb.templates.signoff);
    }
};

export const fetchDropdownOptions = async () => {
    console.log('API: Fetching dropdown options...');
    try {
        const apiData = await apiClient.get('/admin/dropdown-options');
        console.log('Received dropdown options from API:', apiData);

        // The API already returns the data in the correct format
        // No transformation needed since our backend endpoint structures it correctly
        return apiData;
        // Add missing fields to mock data to prevent errors
        const mockOptions = {
            ...mockDb.dropdownOptions,
            sendWhen: {
                ...mockDb.dropdownOptions.sendWhen,
                units: ['day', 'week', 'month'],
                timings: ['before', 'after', 'on']
            }
        };
        return withDelay(mockOptions);
    } catch (error) {
        console.error('Error fetching dropdown options:', error);
        // Add missing fields to mock data to prevent errors
        const mockOptions = {
            ...mockDb.dropdownOptions,
            sendWhen: {
                ...mockDb.dropdownOptions.sendWhen,
                units: ['day', 'week', 'month'],
                timings: ['before', 'after', 'on']
            }
        };
        return withDelay(mockOptions);
    }
};

// --- MUTATE (Write) Operations ---

export const updateDealership = async (dealershipToUpdate) => {
    console.log('API: Updating dealership...', dealershipToUpdate);
    const index = mockDb.dealerships.findIndex(d => d.id === dealershipToUpdate.id);
    if (index !== -1) {
        mockDb.dealerships[index] = dealershipToUpdate;
        return withDelay(dealershipToUpdate);
    }
    throw new Error('Dealership not found');
};

export const updateTemplate = async (templateKey, templateData) => {
    console.log(`API: Updating template '${templateKey}'...`, templateData);
    try {
        // Factfind and links are handled individually via /api/admin/templates/:id
        // No need to call bulk update endpoint for these
        if (templateKey === 'factfind' || templateKey === 'links') {
            console.log(`Template '${templateKey}' updates are handled individually, skipping bulk update`);
            return templateData;
        }
        
        if (templateKey === 'contactPlan') {
            // Handle contact plan template updates differently
            const salesPromises = templateData.sales.map(async (item) => {
                const messageData = {
                    message_type: 'sales',
                    message_name: item.messageName,
                    message_content: item.messageContent || 'Default message content',
                    notes: item.notes,
                    default_send_when: item.sendWhen,
                    default_feed: item.feed,
                    query_config_id: item.queryData?.query_config_id || null,
                    query_mode: item.queryData?.queryMode || null,
                    natural_query: item.queryData?.naturalQuery || null,
                    sql_query: item.queryData?.sqlQuery || null,
                    query_description: item.queryData?.description || null
                };

                if (item.id.startsWith('cp-master-')) {
                    // New item, create it
                    return await apiClient.post('/contact-messages', messageData);
                } else {
                    // Existing item, update it - extract database ID from prefixed ID
                    const dbId = item.id.replace(/^cp-(sales|service)-/, '');
                    return await apiClient.put(`/contact-messages/${dbId}`, messageData);
                }
            });

            const servicePromises = templateData.service.map(async (item) => {
                const messageData = {
                    message_type: 'service',
                    message_name: item.messageName,
                    message_content: item.messageContent || 'Default message content',
                    notes: item.notes,
                    default_send_when: item.sendWhen,
                    default_feed: item.feed,
                    query_config_id: item.queryData?.query_config_id || null,
                    query_mode: item.queryData?.queryMode || null,
                    natural_query: item.queryData?.naturalQuery || null,
                    sql_query: item.queryData?.sqlQuery || null,
                    query_description: item.queryData?.description || null
                };

                if (item.id.startsWith('cp-master-')) {
                    // New item, create it
                    return await apiClient.post('/contact-messages', messageData);
                } else {
                    // Existing item, update it - extract database ID from prefixed ID
                    const dbId = item.id.replace(/^cp-(sales|service)-/, '');
                    return await apiClient.put(`/contact-messages/${dbId}`, messageData);
                }
            });

            await Promise.all([...salesPromises, ...servicePromises]);
            return templateData;
        } else {
            // Other template types - use the original logic
            const result = await apiClient.put('/templates', { templateKey, templateData });
            return result;
            throw new Error('Failed to update template');
        }
    } catch (error) {
        console.error('Error updating template:', error);
        // Fallback to mock behavior
        mockDb.templates[templateKey] = templateData;
        return withDelay(templateData);
    }
};

// Delete contact message template
export const deleteContactMessage = async (messageId) => {
    console.log(`API: Deleting contact message ${messageId}...`);
    try {
        const response = await fetch(`/api/contact-messages/${messageId}`, {
            method: 'DELETE'
        });

        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to delete contact message');
    } catch (error) {
        console.error('Error deleting contact message:', error);
        throw error;
    }
};

// Location-specific contact plans API functions
export const fetchContactPlansForLocation = async (locationId) => {
    console.log(`API: Fetching contact plans for location ${locationId}...`);
    try {
        const response = await fetch(`/api/contact-plans/location/${locationId}`);
        if (response.ok) {
            const plans = await response.json();

            // Transform to expected format
            const salesPlans = plans
                .filter(plan => plan.message_type === 'Sales')
                .map(plan => {
                    let sendWhen = { qty: 1, unit: 'day', timing: 'after', event: 'Contract Signed' };
                    if (plan.send_when) {
                        try {
                            sendWhen = typeof plan.send_when === 'string' ? JSON.parse(plan.send_when) : plan.send_when;
                            // Fix event name format if needed
                            if (sendWhen.event === 'contract_signed') sendWhen.event = 'Contract Signed';
                        } catch (e) {
                            console.error('Error parsing send_when:', e);
                        }
                    }
                    return {
                        id: plan.id,
                        messageName: plan.message_name,
                        sendWhen: sendWhen,
                        notes: plan.notes || '',
                        feed: plan.feed || 'Email',
                        messageContent: plan.message_content
                    };
                });

            const servicePlans = plans
                .filter(plan => plan.message_type === 'Service')
                .map(plan => {
                    let sendWhen = { qty: 1, unit: 'day', timing: 'after', event: 'Service Due' };
                    if (plan.send_when) {
                        try {
                            sendWhen = typeof plan.send_when === 'string' ? JSON.parse(plan.send_when) : plan.send_when;
                            // Fix event name format if needed
                            if (sendWhen.event === 'service_booked') sendWhen.event = 'Service Due';
                        } catch (e) {
                            console.error('Error parsing send_when:', e);
                        }
                    }
                    return {
                        id: plan.id,
                        messageName: plan.message_name,
                        sendWhen: sendWhen,
                        notes: plan.notes || '',
                        feed: plan.feed || 'Email',
                        messageContent: plan.message_content
                    };
                });

            return { sales: salesPlans, service: servicePlans };
        }
        throw new Error('Failed to fetch contact plans for location');
    } catch (error) {
        console.error('Error fetching contact plans for location:', error);
        return { sales: [], service: [] };
    }
};

export const createContactPlan = async (locationId, planData) => {
    console.log(`API: Creating contact plan for location ${locationId}...`);
    try {
        const response = await fetch('/api/contact-plans', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                dealer_location_id: locationId,
                contact_message_id: planData.contact_message_id,
                send_when: JSON.stringify(planData.sendWhen),
                feed: planData.feed,
                notes: planData.notes
            })
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to create contact plan');
    } catch (error) {
        console.error('Error creating contact plan:', error);
        throw error;
    }
};

export const updateContactPlan = async (planId, planData) => {
    console.log(`API: Updating contact plan ${planId}...`);
    try {
        const response = await fetch(`/api/contact-plans/${planId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                send_when: JSON.stringify(planData.sendWhen),
                feed: planData.feed,
                notes: planData.notes,
                query_config_id: planData.queryData?.query_config_id || null,
                query_mode: planData.queryData?.queryMode || null,
                natural_query: planData.queryData?.naturalQuery || null,
                sql_query: planData.queryData?.sqlQuery || null,
                query_description: planData.queryData?.description || null
            })
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to update contact plan');
    } catch (error) {
        console.error('Error updating contact plan:', error);
        throw error;
    }
};

export const deleteContactPlan = async (planId) => {
    console.log(`API: Deleting contact plan ${planId}...`);
    try {
        const response = await fetch(`/api/contact-plans/${planId}`, {
            method: 'DELETE'
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to delete contact plan');
    } catch (error) {
        console.error('Error deleting contact plan:', error);
        throw error;
    }
};

// New template management functions

export const fetchChecklistProgress = async (dealershipId) => {
    console.log(`API: Fetching checklist progress for dealership ${dealershipId}...`);
    try {
        const response = await fetch(`/api/dealerships/${dealershipId}/checklist-progress`);
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to fetch checklist progress');
    } catch (error) {
        console.error('Error fetching checklist progress:', error);
        return { sales: [], service: [] };
    }
};

export const updateChecklistProgress = async (dealershipId, fieldKey, isCompleted, notes = '') => {
    console.log(`API: Updating checklist progress for ${dealershipId}, field ${fieldKey}...`);
    try {
        const response = await fetch(`/api/dealerships/${dealershipId}/checklist-progress`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                checklist_item_id: fieldKey,
                status: isCompleted ? 'completed' : 'pending',
                notes: notes
            })
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to update checklist progress');
    } catch (error) {
        console.error('Error updating checklist progress:', error);
        throw error;
    }
};

export const fetchLocationTemplateData = async (locationId, templateType, category) => {
    console.log(`API: Fetching ${templateType}/${category} data for location ${locationId}...`);
    try {
        const response = await fetch(`/api/locations/${locationId}/template-data/${templateType}/${category}`);
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to fetch location template data');
    } catch (error) {
        console.error('Error fetching location template data:', error);
        return {};
    }
};

export const updateLocationTemplateData = async (locationId, templateType, category, fieldKey, fieldValue) => {
    console.log(`API: Updating ${templateType}/${category} data for location ${locationId}...`);
    try {
        const response = await fetch(`/api/locations/${locationId}/template-data`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ template_type: templateType, category, field_key: fieldKey, field_value: fieldValue })
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to update location template data');
    } catch (error) {
        console.error('Error updating location template data:', error);
        throw error;
    }
};

export const addDealership = async (name) => {
    console.log(`API: Adding dealership '${name}'...`);
    const newDealership = { id: Date.now().toString(), name, locations:[], onboardingStatus: {}, contactPlan: null, signoffStatus: {} };
    mockDb.dealerships.push(newDealership);
    return withDelay(newDealership);
};

export const updateDropdownOptions = async (options) => {
    console.log('API: Updating dropdown options...', options);
    mockDb.dropdownOptions = options;
    return withDelay(options);
};

export const pushTemplateToDealerships = async (moduleKey, dealershipIds) => {
    console.log(`API: Pushing template '${moduleKey}' to dealerships:`, dealershipIds);
    try {
        if (moduleKey === 'contactPlan') {
            // For contact plan, we need to get all location IDs for the selected groups
            const locationIds = [];

            for (const groupId of dealershipIds) {
                // Get all locations for this group
                const response = await fetch(`/api/dealer-locations-with-addresses`);
                if (response.ok) {
                    const locations = await response.json();
                    const groupLocations = locations
                        .filter(loc => loc.group_id === groupId)
                        .map(loc => loc.id);
                    locationIds.push(...groupLocations);
                }
            }

            if (locationIds.length > 0) {
                // Push template to all locations
                const response = await fetch('/api/contact-plans/push-template', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ location_ids: locationIds })
                });

                if (response.ok) {
                    const result = await response.json();
                    console.log('Template pushed successfully:', result);
                    return true;
                }
            }
        }

        // Fallback to mock behavior for other templates
        mockDb.dealerships.forEach(d => {
            if (dealershipIds.includes(d.id) && moduleKey === 'contactPlan') {
                d.contactPlan = null; // Backend logic to reset/apply template
            }
        });
        return withDelay(true);
    } catch (error) {
        console.error('Error pushing template:', error);
        return withDelay(false);
    }
};

export const addDropdownOption = async (optionType, optionValue) => {
    console.log(`API: Adding dropdown option for ${optionType}: ${optionValue}`);
    try {
        const response = await fetch('/api/admin/dropdown-options', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ option_type: optionType, option_value: optionValue })
        });
        if (response.ok) {
            const newOption = await response.json();
            // Return updated options for the type
            return await fetchDropdownOptions();
        }
        throw new Error('Failed to add dropdown option');
    } catch (error) {
        console.error('Error adding dropdown option:', error);
        // Fallback to mock behavior
        if (optionType === 'sendWhenEvents') {
            if (!mockDb.dropdownOptions.sendWhen) mockDb.dropdownOptions.sendWhen = { events: [] };
            if (!mockDb.dropdownOptions.sendWhen.events) mockDb.dropdownOptions.sendWhen.events = [];
            mockDb.dropdownOptions.sendWhen.events.push(optionValue);
        } else {
            if (!mockDb.dropdownOptions[optionType]) {
                mockDb.dropdownOptions[optionType] = [];
            }
            mockDb.dropdownOptions[optionType].push(optionValue);
        }
        return withDelay(mockDb.dropdownOptions);
    }
};

export const deleteDropdownOption = async (optionType, optionValueOrId) => {
    console.log(`API: Deleting dropdown option ${optionValueOrId} for type ${optionType}`);
    try {
        // Try to get the ID from the global mappings if we have a value
        let optionId = optionValueOrId;
        if (global._dropdownIdMappings && global._dropdownIdMappings[optionType] && global._dropdownIdMappings[optionType][optionValueOrId]) {
            optionId = global._dropdownIdMappings[optionType][optionValueOrId];
        }

        const response = await fetch('/api/admin/dropdown-options', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ option_type: optionType, option_value: optionValueOrId })
        });
        if (response.ok) {
            return await fetchDropdownOptions();
        }
        throw new Error('Failed to delete dropdown option');
    } catch (error) {
        console.error('Error deleting dropdown option:', error);
        // Fallback to mock behavior
        if (optionType === 'sendWhenEvents') {
            if (mockDb.dropdownOptions.sendWhen && mockDb.dropdownOptions.sendWhen.events) {
                mockDb.dropdownOptions.sendWhen.events = mockDb.dropdownOptions.sendWhen.events.filter(item => item !== optionValueOrId);
            }
        } else {
            if (mockDb.dropdownOptions[optionType]) {
                mockDb.dropdownOptions[optionType] = mockDb.dropdownOptions[optionType].filter(item => item !== optionValueOrId);
            }
        }
        return withDelay(mockDb.dropdownOptions);
    }
};

export const reorderDropdownOptions = async (optionType, orderedIds) => {
    console.log(`API: Reordering dropdown options for ${optionType}`);
    try {
        const response = await fetch('/api/admin/dropdown-options/reorder', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ option_type: optionType, ordered_values: orderedIds })
        });
        if (response.ok) {
            return await fetchDropdownOptions();
        }
        throw new Error('Failed to reorder dropdown options');
    } catch (error) {
        console.error('Error reordering dropdown options:', error);
        // Fallback to mock behavior
        if (optionType === 'sendWhenEvents') {
            if (!mockDb.dropdownOptions.sendWhen) mockDb.dropdownOptions.sendWhen = { events: [] };
            mockDb.dropdownOptions.sendWhen.events = orderedIds;
        } else {
            mockDb.dropdownOptions[optionType] = orderedIds;
        }
        return withDelay(mockDb.dropdownOptions);
    }
};

export const renameDropdownOption = async (optionType, oldValue, newValue) => {
    console.log(`API: Renaming dropdown option for ${optionType}: from ${oldValue} to ${newValue}`);
    try {
        // Get the ID from the global mappings
        let optionId = oldValue;
        if (global._dropdownIdMappings && global._dropdownIdMappings[optionType] && global._dropdownIdMappings[optionType][oldValue]) {
            optionId = global._dropdownIdMappings[optionType][oldValue];
        }

        const response = await fetch('/api/admin/dropdown-options/rename', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ option_type: optionType, old_value: oldValue, new_value: newValue })
        });
        if (response.ok) {
            return await fetchDropdownOptions();
        }
        throw new Error('Failed to rename dropdown option');
    } catch (error) {
        console.error('Error renaming dropdown option:', error);
        // Return existing options on error
        return await fetchDropdownOptions();
    }
};

export const uploadExcelFile = async (file, type) => {
    console.log(`API: Mock uploading Excel file '${file.name}' for type '${type}'`);
    return withDelay({ success: true, message: `File ${file.name} uploaded.` });
};

export const uploadDealerLocations = async (file) => {
    console.log(`API: Mock uploading dealer locations file '${file.name}'`);
    return withDelay({ success: true, message: `File ${file.name} uploaded.` });
};

export const downloadMasterChecklist = async () => {
    console.log('API: Mock downloading master checklist.');
    const mockExcelContent = 'ID,Category,Task\\n1,Setup,Sign contract';
    const blob = new Blob([mockExcelContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return withDelay(blob);
};

export const downloadDealershipPlan = async (dealershipName) => {
    console.log(`API: Mock downloading plan for '${dealershipName}'.`);
    const mockExcelContent = `Plan for ${dealershipName}`;
    const blob = new Blob([mockExcelContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return withDelay(blob);
};

// --- DEALER LOCATION LINKS API ---

export const fetchDealerLocationLinks = async (locationId) => {
    console.log(`API: Fetching links for dealer location ${locationId}...`);
    try {
        const links = await apiClient.get(`/dealer-locations/${locationId}/links`);

        // Transform API response to expected format
        const salesLinks = links
            .filter(link => link.link_type === 'sales')
            .map(link => ({
                link_key: link.link_key,
                url: link.url,
                link_instance_id: link.link_instance_id
            }));

        const serviceLinks = links
            .filter(link => link.link_type === 'service')
            .map(link => ({
                link_key: link.link_key,
                url: link.url,
                link_instance_id: link.link_instance_id
            }));

        return { sales: salesLinks, service: serviceLinks };
    } catch (error) {
        console.error('Error fetching dealer location links:', error);
        return { sales: [], service: [] };
    }
};

export const createDealerLocationLink = async (locationId, linkId, url) => {
    console.log(`API: Creating link for dealer location ${locationId}...`);
    try {
        const data = await apiClient.post(`/dealer-locations/${locationId}/links`, {
            link_id: linkId,
            custom_url: url
        });
        return data;
    } catch (error) {
        console.error('Error creating dealer location link:', error);
        throw error;
    }
};

export const updateDealerLocationLink = async (locationId, linkInstanceId, url) => {
    console.log(`API: Updating link ${linkInstanceId} for dealer location ${locationId}...`);
    try {
        const data = await apiClient.put(`/dealer-locations/${locationId}/links/${linkInstanceId}`, {
            custom_url: url
        });
        return data;
    } catch (error) {
        console.error('Error updating dealer location link:', error);
        throw error;
    }
};

export const deleteDealerLocationLink = async (locationId, linkInstanceId) => {
    console.log(`API: Deleting link ${linkInstanceId} for dealer location ${locationId}...`);
    try {
        const data = await apiClient.delete(`/dealer-locations/${locationId}/links/${linkInstanceId}`);
        return data;
    } catch (error) {
        console.error('Error deleting dealer location link:', error);
        throw error;
    }
};

// --- ADDRESSES API ---

export const fetchAddresses = async () => {
    console.log('API: Fetching all addresses...');
    try {
        const data = await apiClient.get('/addresses');
        return data;
    } catch (error) {
        console.error('Error fetching addresses:', error);
        return [];
    }
};

export const fetchAddress = async (addressId) => {
    console.log(`API: Fetching address ${addressId}...`);
    try {
        const data = await apiClient.get(`/addresses/${addressId}`);
        return data;
    } catch (error) {
        console.error('Error fetching address:', error);
        throw error;
    }
};

export const createAddress = async (addressData) => {
    console.log('API: Creating new address...');
    try {
        const data = await apiClient.post('/addresses', addressData);
        return data;
    } catch (error) {
        console.error('Error creating address:', error);
        throw error;
    }
};

export const updateAddress = async (addressId, addressData) => {
    console.log(`API: Updating address ${addressId}...`);
    try {
        const data = await apiClient.put(`/addresses/${addressId}`, addressData);
        return data;
    } catch (error) {
        console.error('Error updating address:', error);
        throw error;
    }
};

export const deleteAddress = async (addressId) => {
    console.log(`API: Deleting address ${addressId}...`);
    try {
        const data = await apiClient.delete(`/addresses/${addressId}`);
        return data;
    } catch (error) {
        console.error('Error deleting address:', error);
        throw error;
    }
};

export const fetchDealerLocationsWithAddresses = async () => {
    console.log('API: Fetching dealer locations with their addresses...');
    try {
        const data = await apiClient.get('/dealer-locations-with-addresses');
        return data;
    } catch (error) {
        console.error('Error fetching dealer locations with addresses:', error);
        return [];
    }
};

export const updateDealerLocationAddress = async (locationId, addressId) => {
    console.log(`API: Updating address for dealer location ${locationId}...`);
    try {
        const data = await apiClient.put(`/dealer-locations/${locationId}/address`, {
            address_id: addressId
        });
        return data;
    } catch (error) {
        console.error('Error updating dealer location address:', error);
        throw error;
    }
};

// Template Data API functions
export const fetchDealerLocationTemplateData = async (locationId, templateType = null) => {
    console.log(`API: Fetching template data for location ${locationId}...`);
    try {
        const url = templateType
            ? `/dealer-locations/${locationId}/template-data/${templateType}`
            : `/dealer-locations/${locationId}/template-data`;

        const data = await apiClient.get(url);
        return data;
    } catch (error) {
        console.error('Error fetching dealer location template data:', error);
        // Return empty structure if no data found
        return templateType ? { sales: [], service: [] } : {};
    }
};

export const createDealerLocationTemplateData = async (locationId, fieldKey, fieldValue) => {
    console.log(`API: Creating template data for location ${locationId}, field ${fieldKey}...`);
    try {
        const data = await apiClient.post(`/dealer-locations/${locationId}/template-data`, {
            field_key: fieldKey,
            field_value: fieldValue
        });
        return data;
    } catch (error) {
        console.error('Error creating dealer location template data:', error);
        throw error;
    }
};

export const updateDealerLocationTemplateData = async (locationId, instanceId, fieldValue) => {
    console.log(`API: Updating template data instance ${instanceId} for location ${locationId}...`);
    try {
        const data = await apiClient.put(`/dealer-locations/${locationId}/template-data/${instanceId}`, {
            field_value: fieldValue
        });
        return data;
    } catch (error) {
        console.error('Error updating dealer location template data:', error);
        throw error;
    }
};

export const deleteDealerLocationTemplateData = async (locationId, instanceId) => {
    console.log(`API: Deleting template data instance ${instanceId} for location ${locationId}...`);
    try {
        const data = await apiClient.delete(`/dealer-locations/${locationId}/template-data/${instanceId}`);
        return data;
    } catch (error) {
        console.error('Error deleting dealer location template data:', error);
        throw error;
    }
};

// Master Checklist Management API functions
export const createMasterChecklistItem = async (itemData) => {
    console.log('API: Creating master checklist item...');
    try {
        const response = await fetch('/api/master-checklist', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(itemData)
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to create master checklist item');
    } catch (error) {
        console.error('Error creating master checklist item:', error);
        throw error;
    }
};

export const updateMasterChecklistItem = async (itemId, itemData) => {
    console.log(`API: Updating master checklist item ${itemId}...`);
    try {
        const response = await fetch(`/api/master-checklist/${itemId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(itemData)
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to update master checklist item');
    } catch (error) {
        console.error('Error updating master checklist item:', error);
        throw error;
    }
};

export const deleteMasterChecklistItem = async (itemId) => {
    console.log(`API: Deleting master checklist item ${itemId}...`);
    try {
        const response = await fetch(`/api/master-checklist/${itemId}`, {
            method: 'DELETE'
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to delete master checklist item');
    } catch (error) {
        console.error('Error deleting master checklist item:', error);
        throw error;
    }
};

// --- ADMIN LINKS API ---

export const reorderAdminLinks = async (linkType, orderedIds) => {
    console.log(`API: Reordering admin links for ${linkType}...`);
    try {
        const response = await fetch('/api/admin/links/reorder', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ link_type: linkType, ordered_ids: orderedIds })
        });
        if (response.ok) {
            return await response.json();
        }
        throw new Error('Failed to reorder admin links');
    } catch (error) {
        console.error('Error reordering admin links:', error);
        throw error;
    }
};

